import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBackLayer } from '../../../../ui/backLayer/backLayerContext';

/**
 * Bildet eine UI-Ebene (Viewer, Hinzufügen-Modus, …) auf einen History-Eintrag ab, statt
 * auf lokalen State. `location.state.mobileLayers` (Array aus `{id, payload}`) ist die
 * Quelle der Wahrheit und übersteht damit einen Reload; `open` pusht denselben Pfad mit
 * der um die eigene Ebene erweiterten Liste, `close` navigiert zurück. Ebenen stapeln
 * (z. B. Viewer über Hinzufügen-Modus).
 *
 * `close()` nimmt an, dass diese Ebene die oberste im Stapel ist — die Mobil-Ansicht
 * bietet Ebenen nur in Stapel-Reihenfolge zum Schließen an (Zurück-Geste schließt immer
 * die zuletzt geöffnete). Liegt beim Aufruf noch eine flüchtige Fläche darüber, würde ihr
 * blindes `navigate(-1)` deren Fänger erwischen statt die Ebene — dafür braucht ein
 * Selbstschluss stattdessen `closeLayersDownTo()` des Verwalters.
 */
export function useHistoryLayer(layerId) {
  const location = useLocation();
  const navigate = useNavigate();
  // `useBackLayer()`, nicht `useBackLayerManager()`: die Warnung ist ein Netz, kein
  // Pflichtvertrag, und der Hook läuft auch in Tests ohne montierten Provider.
  const manager = useBackLayer();
  const layers = location.state?.mobileLayers || [];
  const entry = layers.find((l) => l.id === layerId);

  const open = useCallback((payload) => {
    const current = location.state?.mobileLayers || [];
    if (current.some((l) => l.id === layerId)) return;
    // Das Fänger-Merkmal gehört NUR zu dem Eintrag, der es getragen hat: geerbt in einem
    // Ebenen-Eintrag sähe der Verwalter einen Fänger ohne offene Fläche und nähme die
    // gerade geöffnete Ebene sofort wieder zurück.
    const { mobileTransient: _weg, ...restState } = location.state || {};
    navigate(location.pathname + location.search, {
      state: { ...restState, mobileLayers: [...current, { id: layerId, payload: payload ?? null }] },
    });
  }, [navigate, location, layerId]);

  const close = useCallback(() => {
    if (process.env.NODE_ENV !== 'production' && manager?.hasOpenSurfaces?.()) {
      console.warn(
        `Ebene "${layerId}" wird geschlossen, während eine flüchtige Fläche darüber liegt — `
        + 'closeLayersDownTo() benutzen, sonst trifft der Zurück-Schritt den Fänger.'
      );
    }
    if ((location.state?.mobileLayers || []).some((l) => l.id === layerId)) navigate(-1);
  }, [navigate, location, layerId, manager]);

  return { isOpen: Boolean(entry), payload: entry?.payload ?? null, open, close };
}
